'use client';

import React, { useEffect, useRef, useState, type RefObject } from 'react';
import Link from 'next/link';
import { ChevronDown, PanelRightClose, Check } from 'lucide-react';
import { createPortal } from 'react-dom';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import type { ShellTokens } from '@/lib/shell/tokens';
import {
  isActionableItem,
  type ContextRailContent,
  type RailItem,
  type RailSection,
} from '@/lib/shell/contextRailModel';
import type { RailPreference } from '@/lib/shell/railPreferences';

const PREFERENCE_MENU_TITLE = 'Panel behavior';

const PREFERENCE_LABELS: Record<RailPreference, string> = {
  always: 'Show panel automatically',
  remember: 'Remember this page',
  collapsed: 'Start collapsed',
};

function SkeletonRows({ tokens }: { tokens: ShellTokens }) {
  return (
    <div aria-hidden="true" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {[64, 82, 58].map((width, index) => (
        <div
          key={index}
          style={{
            height: 12,
            width: `${width}%`,
            borderRadius: 6,
            background: tokens.surfaceHover,
          }}
        />
      ))}
    </div>
  );
}

function RailItemRow({ item, tokens }: { item: RailItem; tokens: ShellTokens }) {
  const body = (
    <>
      {item.icon ? (
        <span style={{ display: 'flex', flexShrink: 0, color: tokens.textMuted }}>{item.icon}</span>
      ) : null}
      <span style={{ minWidth: 0, flex: 1 }}>
        <span
          style={{
            display: 'block',
            fontSize: 12.5,
            fontWeight: 600,
            color: tokens.textPrimary,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {item.label}
        </span>
        {item.hint ? (
          <span
            style={{
              display: 'block',
              fontSize: 11,
              color: tokens.textMuted,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {item.hint}
          </span>
        ) : null}
      </span>
      {item.badge != null && item.badge > 0 ? (
        <span
          style={{
            fontSize: 10,
            fontWeight: 800,
            minWidth: 18,
            height: 16,
            padding: '0 5px',
            borderRadius: 6,
            background: tokens.accentSoft,
            color: tokens.accentText,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {item.badge > 99 ? '99+' : item.badge}
        </span>
      ) : null}
      {item.value ? (
        <span
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: tokens.textPrimary,
            fontVariantNumeric: 'tabular-nums',
            textAlign: 'right',
            flexShrink: 0,
          }}
        >
          {item.value}
        </span>
      ) : null}
    </>
  );

  const baseStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    padding: '7px 8px',
    borderRadius: 9,
    textAlign: 'left',
    textDecoration: 'none',
    border: '1px solid transparent',
    background: 'transparent',
  };

  if (item.disabled) {
    // A disabled action must explain itself; it is inert but visible.
    return (
      <div aria-disabled="true" style={{ ...baseStyle, opacity: 0.55, cursor: 'default' }}>
        {body}
        {item.disabledReason ? (
          <span style={{ fontSize: 10.5, color: tokens.textSub, flexShrink: 0 }}>
            {item.disabledReason}
          </span>
        ) : null}
      </div>
    );
  }

  if (!isActionableItem(item)) {
    // Static info row: intentionally not a button. No dead controls.
    return <div style={baseStyle}>{body}</div>;
  }

  const interactiveStyle: React.CSSProperties = { ...baseStyle, cursor: 'pointer' };
  const hover = (element: HTMLElement, entering: boolean) => {
    element.style.background = entering ? tokens.surfaceHover : 'transparent';
  };

  if (item.href) {
    return (
      <Link
        href={item.href}
        className="shell-transition shell-hit-target"
        style={interactiveStyle}
        onClick={item.onSelect}
        onMouseEnter={(event) => hover(event.currentTarget, true)}
        onMouseLeave={(event) => hover(event.currentTarget, false)}
      >
        {body}
      </Link>
    );
  }

  return (
    <button
      type="button"
      className="shell-transition shell-hit-target"
      style={interactiveStyle}
      onClick={item.onSelect}
      onMouseEnter={(event) => hover(event.currentTarget, true)}
      onMouseLeave={(event) => hover(event.currentTarget, false)}
    >
      {body}
    </button>
  );
}

function RailSectionBlock({
  section,
  tokens,
  divider,
}: {
  section: RailSection;
  tokens: ShellTokens;
  divider: boolean;
}) {
  return (
    <section
      aria-label={section.title}
      style={{
        padding: '10px 10px 4px',
        borderTop: divider ? `1px solid ${tokens.dividerSoft}` : 'none',
        marginTop: divider ? 4 : 0,
      }}
    >
      <h3
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.07em',
          textTransform: 'uppercase',
          color: tokens.sectionLabel,
          margin: divider ? '6px 0 4px' : '0 0 4px',
          padding: '0 8px',
        }}
      >
        {section.title}
      </h3>
      {section.state === 'loading' ? (
        <div style={{ padding: '4px 8px 8px' }}>
          <SkeletonRows tokens={tokens} />
        </div>
      ) : section.state === 'error' ? (
        <div style={{ padding: '4px 8px 8px' }}>
          <p style={{ fontSize: 12, color: tokens.textMuted, margin: 0 }}>
            {section.errorText || 'This could not be loaded right now.'}
          </p>
          {section.retry ? (
            <button
              type="button"
              onClick={section.retry}
              className="shell-transition"
              style={{
                marginTop: 6,
                padding: '5px 10px',
                borderRadius: 8,
                border: `1px solid ${tokens.border}`,
                background: tokens.surface,
                color: tokens.textPrimary,
                fontSize: 11.5,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Try again
            </button>
          ) : null}
        </div>
      ) : section.state === 'forbidden' ? (
        <p style={{ fontSize: 12, color: tokens.textMuted, margin: 0, padding: '4px 8px 8px' }}>
          {section.emptyText || 'Your role does not include access to this.'}
        </p>
      ) : section.state === 'empty' || section.items.length === 0 ? (
        <p style={{ fontSize: 12, color: tokens.textMuted, margin: 0, padding: '4px 8px 8px' }}>
          {section.emptyText || 'Nothing here yet.'}
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {section.items.map((item) => (
            <RailItemRow key={item.id} item={item} tokens={tokens} />
          ))}
        </div>
      )}
    </section>
  );
}

function PreferenceMenu({
  tokens,
  preference,
  onPreferenceChange,
}: {
  tokens: ShellTokens;
  preference: RailPreference;
  onPreferenceChange: (preference: RailPreference) => void;
}) {
  const [open, setOpen] = useState(false);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setOpen(false);
    };
    const handleEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', handleOutside, true);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleOutside, true);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  const menuWidth = 216;
  let left = 0;
  let top = 0;
  if (anchorRect) {
    const vw = window.innerWidth;
    left = Math.min(Math.max(anchorRect.right - menuWidth, 8), vw - menuWidth - 8);
    top = anchorRect.bottom + 4;
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Context panel display preference"
        title="Context panel display preference"
        onClick={() => {
          setAnchorRect(triggerRef.current?.getBoundingClientRect() ?? null);
          setOpen((value) => !value);
        }}
        className="shell-transition shell-hit-target"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 24,
          height: 24,
          borderRadius: 6,
          border: `1px solid ${tokens.border}`,
          background: tokens.surface,
          color: tokens.textMuted,
          cursor: 'pointer',
          flexShrink: 0,
        }}
      >
        <ChevronDown size={13} aria-hidden="true" />
      </button>
      {open && anchorRect
        ? createPortal(
            <div
              ref={menuRef}
              role="listbox"
              aria-label="Context panel display preference"
              style={{
                position: 'fixed',
                top,
                left,
                width: menuWidth,
                borderRadius: 12,
                background: tokens.surface,
                border: `1px solid ${tokens.border}`,
                boxShadow: '0 8px 32px rgba(0,0,0,0.28)',
                zIndex: 99999,
                overflow: 'hidden',
                padding: '6px 0',
              }}
            >
              <p
                aria-hidden="true"
                style={{
                  margin: 0,
                  padding: '4px 12px 8px',
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: tokens.sectionLabel,
                }}
              >
                {PREFERENCE_MENU_TITLE}
              </p>
              {(Object.keys(PREFERENCE_LABELS) as RailPreference[]).map((option) => {
                const selected = option === preference;
                return (
                  <button
                    key={option}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    onClick={() => {
                      onPreferenceChange(option);
                      setOpen(false);
                      triggerRef.current?.focus();
                    }}
                    className="shell-transition"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 8,
                      fontSize: 12.5,
                      fontWeight: selected ? 700 : 500,
                      color: selected ? tokens.accentText : tokens.textPrimary,
                      background: selected ? tokens.accentSoft : 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                  >
                    <span>{PREFERENCE_LABELS[option]}</span>
                    {selected ? <Check size={13} aria-hidden="true" /> : null}
                  </button>
                );
              })}
            </div>,
            document.body
          )
        : null}
    </>
  );
}

export type ContextRailProps = {
  id: string;
  tokens: ShellTokens;
  mode: 'pinned' | 'overlay';
  open: boolean;
  onClose: () => void;
  content: ContextRailContent | null;
  ariaLabel: string;
  preference: RailPreference;
  onPreferenceChange: (preference: RailPreference) => void;
  returnFocusRef: RefObject<HTMLElement | null>;
};

export default function ContextRail({
  id,
  tokens,
  mode,
  open,
  onClose,
  content,
  ariaLabel,
  preference,
  onPreferenceChange,
  returnFocusRef,
}: ContextRailProps) {
  const railRef = useRef<HTMLElement>(null);
  const isOverlay = mode === 'overlay';

  // Focus is trapped only while the rail is a modal overlay. The pinned rail is
  // ordinary page content and must never trap.
  useFocusTrap(railRef, {
    active: isOverlay && open,
    onEscape: onClose,
    returnFocusRef,
  });

  // Overlay locks background scroll and restores whatever value was there before.
  useEffect(() => {
    if (!isOverlay || !open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOverlay, open]);

  if (!content || !open) return null;

  const lastSection = content.sections[content.sections.length - 1];
  const hasStickyActions = content.sections.length > 1 && lastSection?.kind === 'actions';
  const stickyActions = hasStickyActions ? lastSection : null;
  const scrollSections = hasStickyActions ? content.sections.slice(0, -1) : content.sections;

  const inner = (
    <>
      {tokens.prismEdge && tokens.prismEdge !== 'none' && (
        <div
          aria-hidden="true"
          style={{ height: 2, flexShrink: 0, background: tokens.prismEdge, opacity: 0.8 }}
        />
      )}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '12px 14px 8px',
          borderBottom: `1px solid ${tokens.border}`,
          flexShrink: 0,
        }}
      >
        <h2
          style={{
            flex: 1,
            minWidth: 0,
            margin: 0,
            fontSize: 13,
            fontWeight: 800,
            color: tokens.textPrimary,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {content.title}
        </h2>
        {mode === 'pinned' ? (
          <PreferenceMenu
            tokens={tokens}
            preference={preference}
            onPreferenceChange={onPreferenceChange}
          />
        ) : null}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close context panel"
          className="shell-transition shell-hit-target"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 24,
            height: 24,
            borderRadius: 6,
            border: `1px solid ${tokens.border}`,
            background: tokens.surface,
            color: tokens.textMuted,
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          <PanelRightClose size={13} aria-hidden="true" />
        </button>
      </div>
      <div className="context-rail__scroll" style={{ padding: '4px 4px 16px' }}>
        {scrollSections.map((section, index) => (
          <RailSectionBlock
            key={section.id}
            section={section}
            tokens={tokens}
            divider={index > 0}
          />
        ))}
      </div>
      {/* Actions stay pinned to the bottom of the rail instead of scrolling away --
          the flex column + flex:1 scroller above already reserves this space. */}
      {stickyActions ? (
        <div
          style={{
            flexShrink: 0,
            borderTop: `1px solid ${tokens.dividerSoft}`,
            background: tokens.railSurface,
            padding: '2px 0 8px',
          }}
        >
          <RailSectionBlock section={stickyActions} tokens={tokens} divider={false} />
        </div>
      ) : null}
    </>
  );

  if (isOverlay) {
    return (
      <>
        <button
          type="button"
          aria-label="Close context panel overlay"
          onClick={onClose}
          className="context-rail__backdrop"
          style={{ background: 'rgba(2,6,23,0.55)' }}
        />
        <aside
          ref={railRef}
          id={id}
          role="complementary"
          aria-label={ariaLabel}
          tabIndex={-1}
          data-open="true"
          className="context-rail context-rail--overlay"
          style={{
            background: tokens.railSurface,
            borderLeft: `1px solid ${tokens.railBorder}`,
            boxShadow: '-12px 0 32px rgba(0,0,0,0.25)',
          }}
        >
          {inner}
        </aside>
      </>
    );
  }

  return (
    <aside
      ref={railRef}
      id={id}
      role="complementary"
      aria-label={ariaLabel}
      className="context-rail"
      style={{
        background: tokens.railSurface,
        borderLeft: `1px solid ${tokens.railBorder}`,
      }}
    >
      {inner}
    </aside>
  );
}
